import { FileText } from 'lucide-react';
import { Button, ChatMarkdown, ChatMessage } from '@wundo/ui';
import type { InsightChatItem, InsightChatReport } from '@wundo/shared/insightChat.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';
import { RunStep } from './RunStep.tsx';

export interface RunChatItemProps {
  item: InsightChatItem;
  onOpenReport: (report: InsightChatReport) => void;
}

const REPORT_LABEL: Record<InsightChatReport['action'], string> = {
  published: 'Published the run report',
  corrected: 'Corrected the run report',
  shared: 'New report',
};

/** One thing said or done in a run's conversation. */
export function RunChatItem({ item, onOpenReport }: RunChatItemProps) {
  if (item.kind === 'step') return <RunStep step={item} />;

  if (item.kind === 'question') {
    const name = item.sender?.name ?? 'Former staff member';

    return (
      <div className='flex flex-col items-end gap-1'>
        <p className='text-xs text-ink-muted'>
          {name} · {formatDateTime(item.created_at)}
        </p>
        <ChatMessage author='user' authorLabel={name}>
          <p className='whitespace-pre-wrap'>{item.text}</p>
        </ChatMessage>
      </div>
    );
  }

  if (item.kind === 'answer') {
    return (
      <ChatMessage author='wundo' authorLabel='Analyst'>
        <ChatMarkdown>{item.text}</ChatMarkdown>
      </ChatMessage>
    );
  }

  return (
    <ChatMessage author='wundo' authorLabel='Analyst'>
      <div className='flex flex-col items-start gap-2.5'>
        <p className='flex items-center gap-1.5 text-sm text-ink-muted'>
          <FileText className='size-4' aria-hidden='true' />
          {REPORT_LABEL[item.action]}
        </p>
        {item.title ? <p className='font-bold text-ink'>{item.title}</p> : null}
        <Button variant='quiet' size='sm' onClick={() => onOpenReport(item)}>
          Open report
        </Button>
      </div>
    </ChatMessage>
  );
}
